import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import { useTheme, useMediaQuery } from '@material-ui/core';
import Footer from '@components/Footer';
import Dialog from '@components/Dialog';
import LoadingPresenter from '@components/Loading';
import MyAccountPresenter from './MyAccountPresenter';
import useClient from '@hooks/useClient';
import useSwitchService from '@hooks/useSwitchService';
import {
  useFetchMyQuotationUserQuery,
  CapaQuotationUser,
  CapaServiceType
} from '@generated/graphql';
import { MyAccountContextProps } from './types';
import MyAccountHeader from './components/MyAccountHeader';
import MyAccountSwitchHeader from './components/MyAccountSwitchHeader';
import CommonLayout from '@routes/Common/CommonLayout';
import BottomNav from '@routes/Common/BottomNav';
import ProfileUpdateDialog from '@components/Dialog/Account/ProfileUpdateDialog';

export const MyAccountContext = React.createContext<
  Partial<MyAccountContextProps>
>({});

const MyAccountContainer: React.FC = () => {
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const history = useHistory();
  const { clientState, onUpdateClient, hasRequiredInfo } = useClient();
  const { urlParam }: { urlParam: string } = useParams();
  const accessToken = localStorage.getItem('CLIENT_ACCESS_TOKEN');
  const isAuth = Boolean(clientState?.accessToken);

  const DecodedAccessToken = accessToken
    ? JSON.parse(JSON.stringify(jwt.decode(accessToken)))
    : {};

  //state
  const [current, setCurrent] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [requiredInfoPreDialogOpen, setRequiredInfoPreDialogOpen] = useState<
    boolean
  >(!hasRequiredInfo);

  const [profileUpdateModal, setProfileUpdateModal] = useState(false);

  //data fetch
  const {
    openDuplicatedDialog,
    setOpenDuplicatedDialog,
    handleSwitchService
  } = useSwitchService();
  const { data, loading, refetch } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      onUpdateClient(data.fetchMyQuotationUser.data ?? {});
    },
    skip: clientState.accessToken ? false : true
  });

  const onRefetch = () => {
    refetch();
  };

  //effects
  useEffect(() => {
    const profileBtn = document.getElementById('profile');
    const handleMenuClose = () => setMobileMenuOpen(false);

    if (profileBtn) {
      profileBtn?.addEventListener('click', handleMenuClose);

      return () => {
        profileBtn.removeEventListener('click', handleMenuClose);
      };
    }
  }, []);

  useEffect(() => {
    const gobackBtn = document.getElementById('goback');

    const handleGoback = () => {
      setMobileMenuOpen(false);
      history.push('/mypage');
    };

    if (gobackBtn) {
      gobackBtn.addEventListener('click', handleGoback);
      return () => {
        gobackBtn.removeEventListener('click', handleGoback);
      };
    }
  }, [history]);

  useEffect(() => {
    if (!urlParam) {
      if (!clientState.accessToken) {
        history.push('/login');
        return;
      }

      setCurrent('');
      history.push('/mypage');
    }
    if (urlParam) {
      setCurrent(urlParam);
    }
  }, [urlParam, history, clientState.accessToken]);

  useEffect(() => {
    if (matches && urlParam) {
      setMobileMenuOpen(true);
    }

    return () => {
      setMobileMenuOpen(false);
    };
  }, [matches, urlParam]);

  const getClientQueryRefetch = async () => {
    if (clientState.id) {
      await refetch()
        .then(({ data: { fetchMyQuotationUser } }) => {
          if (fetchMyQuotationUser) {
            onUpdateClient(fetchMyQuotationUser.data as CapaQuotationUser);
          }
        })
        .catch((err) => {});
    }
  };

  const onChangeDrawerStatus = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (loading) return <LoadingPresenter open={loading} />;

  return (
    <CommonLayout
      gnbTabs={isAuth ? null : <></>}
      fullHeight
      bottomNav={matches ? <BottomNav /> : null}
    >
      <Dialog.Account.AlreadyGotPartnerAccountDialog
        open={openDuplicatedDialog}
        onClose={() => setOpenDuplicatedDialog(false)}
      />

      {profileUpdateModal && (
        <ProfileUpdateDialog
          open={profileUpdateModal}
          onClose={() => setProfileUpdateModal(false)}
        />
      )}

      {requiredInfoPreDialogOpen && (
        <Dialog.Account.ProfileUpdatePreviewDialog
          open={requiredInfoPreDialogOpen}
          onClose={() => {
            setRequiredInfoPreDialogOpen(false);
          }}
          onClick={() => {
            setProfileUpdateModal(true);
            setRequiredInfoPreDialogOpen(false);
          }}
        />
      )}

      <MyAccountContext.Provider
        value={{
          current: current,
          data: data?.fetchMyQuotationUser.data || undefined,
          mobileMenuOpen,
          DecodedaccessTokenScopes: DecodedAccessToken?.scopes,
          DecodedaccessTokenEmail: DecodedAccessToken?.aud,
          getClientQueryRefetch: getClientQueryRefetch,
          onChangeDrawerStatus: onChangeDrawerStatus,
          handleSwitchService
        }}
      >
        {!urlParam && matches && (
          <>
            <MyAccountHeader />
            <MyAccountSwitchHeader
              onSwitch={() =>
                handleSwitchService(CapaServiceType.QuotationPartner)
              }
            />
          </>
        )}
        <MyAccountPresenter refetch={onRefetch} />
      </MyAccountContext.Provider>

      {!matches ? <Footer /> : null}
    </CommonLayout>
  );
};
export default MyAccountContainer;
